import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { loadConfig } from "../helpers/config";
import LoadingSpinner from "../components/LoadingSpinner";

function getLastGeprueft(license) {
  if (!license.checks || license.checks.length === 0) return null;
  return [...license.checks].sort(
    (a, b) => new Date(b.check_date) - new Date(a.check_date)
  )[0];
}

function isOverSixMonthsAgo(dateStr) {
  const d = new Date(dateStr);
  const threshold = new Date();
  threshold.setMonth(threshold.getMonth() - 6);
  return d < threshold;
}

function getLicenseCategory(license) {
  if (license.status === "entzogen") return "entzogen";
  const last = getLastGeprueft(license);
  if (!last) return "ueberfaellig";
  return isOverSixMonthsAgo(last.check_date) ? "ueberfaellig" : "aktiv";
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("de-DE");
}

function addMonths(date, months) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

function Licenses() {
  const navigate = useNavigate();
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("alle");

  useEffect(() => {
    async function fetchData() {
      const config = await loadConfig();
      try {
        const res = await axios.get(`${config.APIURL}/licenses`, {
          headers: { accessToken: localStorage.getItem("accessToken") },
        });
        setLicenses(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const filtered = licenses.filter((l) => {
    const cat = getLicenseCategory(l);
    if (filter === "alle") return true;
    if (filter === "aktiv") return cat === "aktiv";
    if (filter === "ueberfaellig") return cat === "ueberfaellig";
    if (filter === "entzogen") return cat === "entzogen";
    return true;
  });

  const tabs = [
    { key: "alle", label: "Alle" },
    { key: "aktiv", label: "Vorgezeigt / Aktiv" },
    { key: "ueberfaellig", label: "Überfällig" },
    { key: "entzogen", label: "Entzogen" },
  ];

  if (loading) return <LoadingSpinner text="Lade Führerscheine..." />;

  return (
    <div className="container page-offset-80">
      <div className="card">
        <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Führerscheinkontrolle</span>
          <button
            className="btn btn-primary btn-small"
            onClick={() => navigate("/licenses/create")}
          >
            + Neuen Führerschein hinzufügen
          </button>
        </div>
        <div className="card-body">
          {/* Filter tabs */}
          <div className="admin-submenu" style={{ marginBottom: "20px" }}>
            {tabs.map((tab) => (
              <button
                key={tab.key}
                className={`admin-tab${filter === tab.key ? " admin-tab-active" : ""}`}
                onClick={() => setFilter(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className="empty-state">
              <p>Keine Einträge gefunden.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-stack">
                <thead>
                  <tr>
                    <th>Name des Inhabers</th>
                    <th>Führerscheinnummer</th>
                    <th>Letzte Kontrolle</th>
                    <th>Nächste Fälligkeit</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((license) => {
                    const cat = getLicenseCategory(license);
                    const lastCheck = getLastGeprueft(license);
                    const nextDue = lastCheck
                      ? addMonths(lastCheck.check_date, 6)
                      : null;

                    let rowStyle = {};
                    let badge = null;
                    if (cat === "aktiv") {
                      rowStyle = { borderLeft: "4px solid #28a745" };
                      badge = (
                        <span className="status-badge status-active">Aktiv</span>
                      );
                    } else if (cat === "ueberfaellig") {
                      rowStyle = { borderLeft: "4px solid #ffc107", backgroundColor: "#fffbea" };
                      badge = (
                        <span
                          className="status-badge"
                          style={{ backgroundColor: "#fff3cd", color: "#856404" }}
                        >
                          Überfällig
                        </span>
                      );
                    } else {
                      rowStyle = { borderLeft: "4px solid #dc3545", backgroundColor: "#fff5f5" };
                      badge = (
                        <span className="status-badge status-inactive">Entzogen</span>
                      );
                    }

                    return (
                      <tr
                        key={license.id}
                        style={{ ...rowStyle, cursor: "pointer" }}
                        onClick={() => navigate(`/licenses/${license.id}`)}
                      >
                        <td data-label="Name des Inhabers">{license.holder_name}</td>
                        <td data-label="Führerschein­nummer">
                          {license.license_number
                            ? license.license_number
                            : license.encryptedNumber
                              ? <span title="Ende-zu-Ende verschlüsselt – auf der Detailseite mit Private Key lesbar">🔒 verschlüsselt</span>
                              : "—"}
                        </td>
                        <td data-label="Letzte Kontrolle">{lastCheck ? formatDate(lastCheck.check_date) : "—"}</td>
                        <td data-label="Nächste Fälligkeit">{nextDue ? formatDate(nextDue) : "—"}</td>
                        <td data-label="Status">{badge}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Licenses;
